import { ComponentProps } from 'react'

import { DocumentNode } from '@apollo/client'
import { Box } from '@factorialco/deprecated-design-system'
import { F0Dialog } from '@factorialco/f0-react'
import { Input } from '@factorialco/f0-react/dist/experimental'
import { DetailsItemsList } from '@factorialco/f0-react/dist/experimental'
import { CreateProjectFromQuoteDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'
import app from 'modules/core/store/models/app'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'

interface ConvertToProjectModalFormValues {
  projectName: string
  employeeId: number | undefined
}

interface ConvertToProjectModalProps {
  isOpen: boolean
  onClose: () => void
  currency: string
  quoteId: number
  defaultProjectName: string
  refetchQueries: DocumentNode[]
  totalBudget: number
}

export const ConvertToProjectModal = ({
  isOpen,
  onClose,
  currency,
  quoteId,
  defaultProjectName,
  refetchQueries,
  totalBudget,
}: ConvertToProjectModalProps) => {
  const {
    access: { locale },
  } = useCurrentSession()
  const [createProjectFromQuote] = useMutation(CreateProjectFromQuoteDocument, {
    extractorFn: (data) => data.financeMutations.convertToProjectFinanceQuote,
    refetchQueries,
  })

  const details: ComponentProps<typeof DetailsItemsList>['details'] = [
    {
      title: i18n.t('finance.quotes.form.convert_to_project.total_hours'),
      content: {
        text: 'XXXXh',
        type: 'item',
      },
    },
    {
      title: i18n.t('finance.quotes.form.convert_to_project.estimated_cost'),
      content: {
        text: 'XXXXX €',
        type: 'item',
      },
    },
    {
      title: i18n.t('finance.quotes.form.convert_to_project.total_budget'),
      content: {
        text: MoneyWithCurrency({
          optionalDecimals: true,
          amount: totalBudget,
          currency,
          locale,
        }),
        type: 'item',
      },
    },
    {
      title: i18n.t('finance.quotes.form.convert_to_project.profit_margin'),
      content: [
        {
          text: 'XXXXX €',
          type: 'item',
        },
        {
          text: 'XX%',
          type: 'item',
        },
      ],
    },
  ]

  const form = useForm<ConvertToProjectModalFormValues>({
    defaultValues: {
      projectName: defaultProjectName,
      employeeId: undefined,
    },
    onSubmit: async (values) => {
      if (!values.projectName || !values.employeeId) {
        const errors: Partial<Record<keyof ConvertToProjectModalFormValues, string>> = {}
        if (!values.projectName) {
          errors.projectName = i18n.t('common.validations.empty')
        }
        if (!values.employeeId) {
          errors.employeeId = i18n.t('common.validations.empty')
        }
        form.setErrors(errors)
        return {
          success: false,
          errors: [],
        }
      }

      const response = await createProjectFromQuote({
        variables: {
          id: Number(quoteId),
          projectName: values.projectName,
          ownerEmployeeId: values.employeeId,
        },
      })

      const handled = handleResult(response, (parsedErrors) => {
        if (parsedErrors.projectName || parsedErrors.employeeId) {
          form.setErrors({
            projectName: parsedErrors.projectName,
            employeeId: parsedErrors.employeeId,
          })
        }
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('errors.generic_saving'),
        })
      }

      onClose()
      return handled
    },
  })

  return (
    <F0Dialog
      isOpen={isOpen}
      onClose={onClose}
      width="sm"
      title={i18n.t('finance.quotes.actions.convert_to_project')}
      primaryAction={{
        label: i18n.t('finance.quotes.actions.create_project'),
        onClick: () => form.handleSubmit(),
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: onClose,
      }}
    >
      <Box gap="s24" width="full">
        <Input
          required
          label={i18n.t('finance.quotes.form.convert_to_project.project_name')}
          type="text"
          size="md"
          value={form.values.projectName || ''}
          onChange={(value) => form.setValues({ ...form.values, projectName: value })}
          error={form.errors.projectName}
          placeholder={i18n.t('common.name')}
        />
        <EmployeeSelectorV2
          label={i18n.t('finance.quotes.form.convert_to_project.project_owner')}
          onChange={(employeeId) => form.setValues({ ...form.values, employeeId })}
          value={form.values.employeeId}
          error={form.errors.employeeId}
          mandatory
        />
        <DetailsItemsList
          details={details}
          tableView
          title={i18n.t('finance.quotes.form.convert_to_project.project_summary')}
        />
      </Box>
    </F0Dialog>
  )
}
